/**
 * The sign-out route accepts an optional `to` search param so that specific
 * flows (e.g. "Sign up with a different email") can redirect users to a page
 * other than the default login page after signing out.
 *
 * To prevent open-redirect attacks a bad actor might inject via the `to` param
 * (e.g. `?to=//evil.com` or `?to=javascript:alert(1)`), we only accept plain
 * relative paths that:
 *   - are strings
 *   - start with `/`
 *   - do not start with `//` (blocks protocol-relative URLs like `//evil.com`)
 *   - do not contain `:` (blocks protocol schemes like `javascript:` or `https:`)
 *   - are not a sign-out page (prevents an infinite redirect loop)
 */
export const SIGN_OUT_FALLBACK = '/login';

/**
 * Every route that signs a user out. All three are excluded: the per-role
 * routes forward `to` on to `/sign-out`, so allowing any of them as a
 * destination would loop, whichever one you entered by.
 */
const SIGN_OUT_PATHS = new Set([
  '/sign-out',
  '/participants/sign-out',
  '/researchers/sign-out',
]);

export function validateSignOutRedirectTo(to: unknown): string {
  const isSafe =
    typeof to === 'string' &&
    to.startsWith('/') &&
    !to.startsWith('//') &&
    !to.includes(':') &&
    !SIGN_OUT_PATHS.has(to);
  return isSafe ? to : SIGN_OUT_FALLBACK;
}
